import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import hyLocale from 'i18n-iso-countries/langs/hy.json';
import ruLocale from 'i18n-iso-countries/langs/ru.json';
import {
    defaultCountries,
    FlagImage,
    getActiveFormattingMask,
    parseCountry,
    usePhoneInput,
    type CountryData,
    type CountryIso2,
    type ParsedCountry,
    type PhoneInputProps as InternationalPhoneInputProps,
    type PhoneInputRefType,
} from 'react-international-phone';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover/Popover';
import { cn } from '../../../utils/cn';
import {
    FORM_ERROR_CLASS,
    FORM_LABEL_CLASS,
    INPUT_BASE_CLASS,
    INPUT_ERROR_CLASS,
} from '../formControlStyles';
import chevronDown from '../../../assets/svgs/chevron-down.svg';
import searchIcon from '../../../assets/svgs/search-icon.svg';

countries.registerLocale(enLocale);
countries.registerLocale(hyLocale);
countries.registerLocale(ruLocale);

const APP_LANGUAGE_TO_COUNTRY_LOCALE: Record<string, string> = {
    am: 'hy',
    en: 'en',
    ru: 'ru',
};

const DEFAULT_PREFERRED_COUNTRIES: CountryIso2[] = ['am', 'ru'];

const PHONE_INPUT_WRAPPER_CLASS =
    'flex min-h-12 w-full items-stretch overflow-hidden rounded-md border border-custom-border-primary bg-white text-sm !leading-relaxed text-custom-text-primary transition-colors focus-within:shadow-none focus-within:ring-offset-0 focus-within:ring-custom-blue-primary disabled:cursor-not-allowed';

type CountryOption = ParsedCountry & {
    localizedName: string;
    searchText: string;
};

export type PhoneFieldProps = {
    label?: string;
    id: string;
    error?: string;
    showError?: boolean;
    className?: string;
    autoComplete?: string;
    readOnly?: boolean;
    searchPlaceholder?: string;
    emptySearchMessage?: string;
} & Omit<InternationalPhoneInputProps, 'inputProps'> & {
        inputProps?: InternationalPhoneInputProps['inputProps'];
    };

function getCountryLocale(language?: string) {
    const baseLanguage = language?.split('-')[0] ?? 'am';
    return APP_LANGUAGE_TO_COUNTRY_LOCALE[baseLanguage] ?? 'en';
}

function getLocalizedCountryName(country: ParsedCountry, locale: string) {
    return countries.getName(country.iso2.toUpperCase(), locale) ?? country.name;
}

function normalizeSearchValue(value: string, locale: string) {
    return value.trim().toLocaleLowerCase(locale);
}

const PhoneField = forwardRef<PhoneInputRefType, PhoneFieldProps>(
    (
        {
            label,
            id,
            error,
            showError = false,
            className,
            autoComplete = 'tel',
            defaultCountry = 'am',
            forceDialCode = true,
            countries: countryData = defaultCountries,
            preferredCountries = DEFAULT_PREFERRED_COUNTRIES,
            prefix,
            defaultMask,
            charAfterDialCode,
            historySaveDebounceMS,
            disableCountryGuess,
            disableDialCodePrefill,
            disableDialCodeAndPrefix = true,
            disableFormatting,
            hideDropdown,
            countrySelectorStyleProps,
            inputClassName,
            inputStyle,
            inputProps,
            style,
            disabled,
            readOnly,
            name,
            required,
            autoFocus,
            placeholder,
            onFocus,
            onBlur,
            onChange,
            disableFocusAfterCountrySelect,
            searchPlaceholder,
            emptySearchMessage,
            ...phoneInputProps
        },
        ref
    ) => {
        const { i18n, t } = useTranslation();
        const [open, setOpen] = useState(false);
        const [search, setSearch] = useState('');
        const [activeCountryIndex, setActiveCountryIndex] = useState(0);
        const [fieldWidth, setFieldWidth] = useState<number>();
        const wrapperRef = useRef<HTMLDivElement | null>(null);
        const searchInputRef = useRef<HTMLInputElement | null>(null);
        const hasError = showError && Boolean(error);
        const errorId = `${id}-error`;
        const countryLocale = getCountryLocale(i18n.language);
        const inputId = inputProps?.id ?? id;
        const inputName = inputProps?.name ?? name;
        const inputRequired = inputProps?.required ?? required;
        const inputAutoFocus = inputProps?.autoFocus ?? autoFocus;
        const inputAutoComplete = inputProps?.autoComplete ?? autoComplete;
        const inputDisabled = inputProps?.disabled ?? disabled;
        const inputReadOnly = inputProps?.readOnly ?? readOnly;
        const inputPlaceholder = inputProps?.placeholder ?? placeholder;
        const inputDescribedBy = hasError ? errorId : inputProps?.['aria-describedby'];

        const { phone, inputValue, country, setCountry, handlePhoneValueChange, inputRef } =
            usePhoneInput({
                defaultCountry,
                value: phoneInputProps.value,
                countries: countryData,
                preferredCountries,
                prefix,
                defaultMask,
                charAfterDialCode,
                historySaveDebounceMS,
                disableCountryGuess,
                disableDialCodePrefill,
                forceDialCode,
                disableDialCodeAndPrefix,
                disableFormatting,
                onChange: (data) => {
                    onChange?.(data.phone, {
                        country: data.country,
                        inputValue: data.inputValue,
                    });
                },
            });

        useImperativeHandle(
            ref,
            () =>
                Object.assign(inputRef.current as HTMLInputElement, {
                    setCountry,
                    state: { phone, inputValue, country },
                }),
            [country, inputRef, inputValue, phone, setCountry]
        );

        const countryOptions = useMemo<CountryOption[]>(() => {
            const parsedCountries = (countryData as CountryData[]).map((countryItem) =>
                parseCountry(countryItem)
            );

            const uniquePreferredCountries = [...new Set(preferredCountries)];
            const countriesByIso = new Map(
                parsedCountries.map((countryItem) => [countryItem.iso2, countryItem])
            );
            const preferredCountrySet = new Set(uniquePreferredCountries);
            const orderedCountries = [
                ...uniquePreferredCountries
                    .map((iso2) => countriesByIso.get(iso2))
                    .filter((countryItem): countryItem is ParsedCountry => Boolean(countryItem)),
                ...parsedCountries.filter(
                    (countryItem) => !preferredCountrySet.has(countryItem.iso2)
                ),
            ];

            return orderedCountries.map((countryItem) => {
                const localizedName = getLocalizedCountryName(countryItem, countryLocale);

                return {
                    ...countryItem,
                    localizedName,
                    searchText: normalizeSearchValue(
                        `${localizedName} ${countryItem.name} ${countryItem.iso2} +${countryItem.dialCode}`,
                        countryLocale
                    ),
                };
            });
        }, [countryData, countryLocale, preferredCountries]);

        const filteredCountries = useMemo(() => {
            const normalizedSearch = normalizeSearchValue(search, countryLocale);

            if (!normalizedSearch) {
                return countryOptions;
            }

            return countryOptions.filter((countryItem) =>
                countryItem.searchText.includes(normalizedSearch)
            );
        }, [countryLocale, countryOptions, search]);

        const maskPlaceholder = useMemo(() => {
            const activeMask = getActiveFormattingMask({
                phone: inputValue,
                country,
                defaultMask,
                disableFormatting,
            });

            return activeMask.replace(/\./g, '0');
        }, [country, defaultMask, disableFormatting, inputValue]);

        useEffect(() => {
            const element = wrapperRef.current;
            if (!element) return;

            const updateWidth = () => setFieldWidth(element.clientWidth);
            updateWidth();

            if (typeof ResizeObserver === 'undefined') {
                return;
            }

            const resizeObserver = new ResizeObserver(updateWidth);
            resizeObserver.observe(element);

            return () => resizeObserver.disconnect();
        }, []);

        useEffect(() => {
            if (!open) {
                setSearch('');
                setActiveCountryIndex(0);
                return;
            }

            const timeoutId = window.setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);

            return () => clearTimeout(timeoutId);
        }, [open]);

        useEffect(() => {
            setActiveCountryIndex(0);
        }, [search]);

        useEffect(() => {
            if (!filteredCountries.length) {
                setActiveCountryIndex(0);
                return;
            }

            setActiveCountryIndex((currentIndex) =>
                Math.min(currentIndex, filteredCountries.length - 1)
            );
        }, [filteredCountries.length]);

        const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
            handlePhoneValueChange(event);
        };

        const handleCountrySelect = (nextCountry: CountryIso2) => {
            setCountry(nextCountry, { focusOnInput: !disableFocusAfterCountrySelect });
            setOpen(false);
        };

        const handleCountryListKeyDown = (
            event: KeyboardEvent<HTMLInputElement | HTMLButtonElement>
        ) => {
            if (!filteredCountries.length) {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    setOpen(false);
                }
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveCountryIndex((currentIndex) =>
                    Math.min(currentIndex + 1, filteredCountries.length - 1)
                );
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveCountryIndex((currentIndex) => Math.max(currentIndex - 1, 0));
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                handleCountrySelect(filteredCountries[activeCountryIndex].iso2);
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
            }
        };

        return (
            <div className={cn('w-full', className)}>
                {label && (
                    <label htmlFor={inputId} className={FORM_LABEL_CLASS}>
                        {label}
                    </label>
                )}

                <Popover open={open} onOpenChange={setOpen}>
                    <div
                        ref={wrapperRef}
                        style={style}
                        className={cn(
                            'flex min-h-12 w-full items-stretch',
                            disabled && 'cursor-not-allowed opacity-60',
                            countrySelectorStyleProps?.className
                        )}
                    >
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                disabled={disabled || readOnly || hideDropdown}
                                className={cn(
                                    INPUT_BASE_CLASS,
                                    hasError && INPUT_ERROR_CLASS,
                                    'relative z-0 flex w-auto shrink-0 items-center gap-2 rounded-r-none border-r-0 px-3 transition md:hover:bg-custom-gray-100 focus-visible:z-10 disabled:hover:bg-white',
                                    countrySelectorStyleProps?.buttonClassName
                                )}
                                aria-label={t('phoneField.selectCountry')}
                            >
                                <FlagImage
                                    iso2={country.iso2}
                                    size={24}
                                    className={cn(
                                        'country-flag shrink-0 rounded-sm',
                                        countrySelectorStyleProps?.flagClassName
                                    )}
                                    style={countrySelectorStyleProps?.flagStyle}
                                />
                                <span
                                    className={cn(
                                        'whitespace-nowrap text-sm !leading-relaxed text-custom-text-placeholder',
                                        hasError && 'text-custom-error-primary'
                                    )}
                                >
                                    +{country.dialCode}
                                </span>
                                {!hideDropdown ? (
                                    <img
                                        src={chevronDown}
                                        alt=""
                                        className={cn(
                                            'ml-auto text-custom-text-placeholder transition-transform',
                                            open && 'rotate-180',
                                            countrySelectorStyleProps?.dropdownArrowClassName
                                        )}
                                        style={countrySelectorStyleProps?.dropdownArrowStyle}
                                        aria-hidden
                                    />
                                ) : null}
                            </button>
                        </PopoverTrigger>

                        <span className="my-3 w-px shrink-0 bg-custom-border-primary" />

                        <input
                            {...inputProps}
                            ref={inputRef}
                            id={inputId}
                            name={inputName}
                            value={inputValue}
                            type="tel"
                            required={inputRequired}
                            autoFocus={inputAutoFocus}
                            autoComplete={inputAutoComplete}
                            disabled={inputDisabled}
                            readOnly={inputReadOnly}
                            placeholder={inputPlaceholder ?? maskPlaceholder}
                            onFocus={inputProps?.onFocus ?? onFocus}
                            onBlur={inputProps?.onBlur ?? onBlur}
                            onChange={handleInputChange}
                            style={{ ...inputStyle, ...inputProps?.style }}
                            className={cn(
                                INPUT_BASE_CLASS,
                                hasError && INPUT_ERROR_CLASS,
                                'min-w-0 flex-1 rounded-l-none border-l-0',
                                inputClassName,
                                inputProps?.className
                            )}
                            aria-invalid={hasError}
                            aria-describedby={inputDescribedBy}
                        />
                    </div>

                    {!hideDropdown ? (
                        <PopoverContent
                            align="start"
                            className="max-h-40 md:max-h-64 lg:max-h-80 overflow-hidden p-0 bg-gray-50 flex flex-col"
                            style={{ width: fieldWidth }}
                        >
                            <div className="px-4 py-2 md:p-4">
                                <div className="relative">
                                    <input
                                        // readOnly
                                        ref={searchInputRef}
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        onKeyDown={handleCountryListKeyDown}
                                        placeholder={
                                            searchPlaceholder ?? t('phoneField.searchPlaceholder')
                                        }
                                        className={cn(INPUT_BASE_CLASS, 'h-9 md:h-12 pr-12')}
                                    />
                                    <img
                                        src={searchIcon}
                                        alt=""
                                        className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 md:h-5 md:w-5 -translate-y-1/2"
                                        aria-hidden
                                    />
                                </div>
                            </div>
                            <div className="max-h-full overflow-y-auto py-1.5 md:py-2">
                                {filteredCountries.length ? (
                                    filteredCountries.map((countryItem) => {
                                        const optionIndex = filteredCountries.indexOf(countryItem);
                                        const selected = countryItem.iso2 === country.iso2;
                                        const active = optionIndex === activeCountryIndex;

                                        return (
                                            <button
                                                key={countryItem.iso2}
                                                type="button"
                                                tabIndex={active ? 0 : -1}
                                                className={cn(
                                                    'flex w-full items-center gap-5 px-4 py-2 text-left text-base transition md:hover:bg-custom-blue-primary/5',
                                                    (selected || active) &&
                                                        'bg-custom-blue-primary/5'
                                                )}
                                                onFocus={() => setActiveCountryIndex(optionIndex)}
                                                onKeyDown={handleCountryListKeyDown}
                                                onClick={() =>
                                                    handleCountrySelect(countryItem.iso2)
                                                }
                                            >
                                                <FlagImage
                                                    iso2={countryItem.iso2}
                                                    size={24}
                                                    className="country-flag shrink-0 rounded-sm"
                                                />
                                                <span className="w-11 text-right shrink-0 text-custom-text-placeholder">
                                                    +{countryItem.dialCode}
                                                </span>
                                                <span className="min-w-0 flex-1 truncate text-sm font-medium text-custom-gray-800">
                                                    {countryItem.localizedName}
                                                </span>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <p className="px-8 py-6 text-sm text-custom-text-placeholder">
                                        {emptySearchMessage ?? t('phoneField.emptySearchMessage')}
                                    </p>
                                )}
                            </div>
                        </PopoverContent>
                    ) : null}
                </Popover>

                {hasError ? (
                    <p id={errorId} className={FORM_ERROR_CLASS} role="alert">
                        {error}
                    </p>
                ) : null}
            </div>
        );
    }
);

PhoneField.displayName = 'PhoneField';

export default PhoneField;
